export type TradeStatus = "open" | "closed";
export type TradeSource = "imported" | "manual";
export type LegKind = "buy" | "sell";

export type RuleCategory =
  | "situational_awareness"
  | "risk_management"
  | "entry_trigger"
  | "exit_trigger";

export const RULE_CATEGORIES: { key: RuleCategory; label: string }[] = [
  { key: "situational_awareness", label: "Situational Awareness" },
  { key: "risk_management", label: "Risk Management" },
  { key: "entry_trigger", label: "Entry Trigger" },
  { key: "exit_trigger", label: "Exit Trigger" },
];

export interface KiteStatus {
  tradebook_id: number;
  tradebook: string;
  has_key: boolean;
  has_secret: boolean;
  connected: boolean;
  expires_at: string | null;
  last_sync_at: string | null;
  last_sync_note: string | null;
}

export interface Tradebook {
  id: number;
  name: string;
  broker: string;
  client_id: string | null;
  initial_capital: number;
  risk_per_trade_pct: number;
  max_position_pct: number;
  max_open_positions: number;
  is_active: boolean;
  kite_api_key: string | null;
  kite_secret_set: boolean;
  net_deposits: number;
  capital_base: number;
  realized_pnl: number;
  current_balance: number;
  open_positions: number;
  closed_trades: number;
}

export interface Leg {
  id: number;
  kind: LegKind;
  leg_date: string;
  leg_time: string | null;
  product: string;
  quantity: number;
  price: number;
  exchange: string | null;
  note: string | null;
}

export interface Journal {
  sl_price: number | null;
  strategy: string | null;
  setup_tag: string | null;
  product: string | null;
  thesis: string | null;
  exit_reason: string | null;
  mistakes: string | null;
  additional_notes: string | null;
  conviction: number | null;
  playbook_id: number | null;
  screenshot_path: string | null;
  rule_ids: number[];
}

export interface Trade {
  id: number;
  tradebook_id: number;
  match_key: string;
  symbol: string;
  entry_date: string;
  exit_date: string | null;
  qty: number;
  avg_entry: number;
  avg_exit: number | null;
  gross_pnl: number | null;
  charges: number | null;
  net_pnl: number | null;
  pct_return: number | null;
  holding_days: number | null;
  status: TradeStatus;
  source: TradeSource;
  anomaly: string | null;
  legs: Leg[];
  journal: Journal | null;
  risk_rupees: number | null;
  risk_pct: number | null;
  rr_multiple: number | null;
  mae: number | null;
  mfe: number | null;
  lowest_price: number | null;
  highest_price: number | null;
  portfolio_pnl_pct: number | null;
  exchange: string | null;
  product: string | null;
}

export const EXCHANGES = ["NSE", "BSE"] as const;
export const PRODUCTS = [
  { key: "cash", label: "Cash" },
  { key: "mtf", label: "MTF" },
] as const;

export interface ImportBatch {
  id: number;
  tradebook_id: number;
  tradebook: string;
  filename: string | null;
  broker: string;
  detected_client_id: string | null;
  row_count: number;
  live_rows: number;
  imported_at: string;
  first_trade_date: string | null;
  last_trade_date: string | null;
}

export interface ExecutionRow {
  id: number;
  tradebook_id: number;
  symbol: string;
  trade_date: string;
  exchange: string | null;
  trade_type: LegKind;
  quantity: number;
  price: number;
  trade_id: string;
  order_execution_time: string | null;
  batch_id: number | null;
  edited: boolean;
  original: Record<string, unknown> | null;
}

export interface Rule {
  id: number;
  category: RuleCategory;
  text: string;
  sort_order: number;
  is_archived: boolean;
}

export interface ImportPreview {
  tradebook: string;
  broker: string;
  total_rows: number;
  new_rows: number;
  duplicate_rows: number;
  rejected_rows: number;
  symbols: string[];
  first_trade_date: string | null;
  last_trade_date: string | null;
  detected_client_id: string | null;
  warnings: string[];
  blockers: string[];
  rejected: { line: number; reason: string }[];
  can_commit: boolean;
  adoptable_client_id: string | null;
  suggested_tradebook_id: number | null;
  suggested_tradebook: string | null;
}

export interface PositionLot {
  entry_trade_id: string;
  entry_date: string;
  quantity: number;
  price: number;
  sl_price: number | null;
  target_price: number | null;
  note: string | null;
  unrealized_pnl: number | null;
  unrealized_pct: number | null;
  initial_risk: number | null;
  open_risk: number | null;
}

export interface OpenPosition {
  trade_id: number;
  tradebook_id: number;
  tradebook: string;
  symbol: string;
  qty: number;
  avg_entry: number;
  entry_date: string;
  days_held: number;
  last_price: number | null;
  price_date: string | null;
  day_change_pct: number | null;
  day_pnl: number | null;
  cost_basis: number;
  position_value: number | null;
  sizing_pct: number;
  sl_price: number | null;
  sl_pct: number | null;
  risk_rupees: number | null;
  risk_pct: number | null;
  open_risk_rupees: number | null;
  open_risk_pct: number | null;
  lots: PositionLot[];
  averaging_down: boolean;
  unrealized_pnl: number | null;
  unrealized_pct: number | null;
  mae_pct: number | null;
  mfe_pct: number | null;
  given_back_pct: number | null;
  portfolio_gain_pct: number | null;
  r_multiple: number | null;
  strategy: string | null;
  sector: string | null;
  industry: string | null;
  basic_industry: string | null;
  product: string;
  anomaly: string | null;
  stale_price: boolean;
}

export interface PositionsResponse {
  tradebook: string;
  capital_base: number;
  positions: OpenPosition[];
  net_risk_pct: number;
  net_unrealized_pnl: number;
  net_unrealized_pct: number;
  net_unrealized_cost_pct: number | null;
  invested: number;
  deployed_pct: number;
  mtf_exposure: number;
  mtf_pct: number;
  mtf_positions: number;
  final_total_capital: number;
  priced: number;
  unpriced: number;
  priced_at: string | null;
}

export interface Candle {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number | null;
}

export interface ChartData {
  symbol: string;
  exchange: string;
  candles: Candle[];
  entries: { date: string; price: number; qty: number }[];
  exits: { date: string; price: number; qty: number }[];
  avg_entry: number | null;
  avg_exit: number | null;
  sl_price: number | null;
  lowest_price: number | null;
  highest_price: number | null;
}

export interface Instrument {
  id: number;
  symbol: string;
  name: string | null;
  isin: string | null;
  series: string | null;
  sector: string | null;
  industry: string | null;
}

export interface StrategyOption {
  id: number;
  name: string;
  description: string | null;
  sort_order: number;
  is_archived: boolean;
}

export interface Bucket {
  key: string;
  trades: number;
  net_pnl: number;
  net_pnl_pct: number | null;
  win_rate: number | null;
}

export interface Dashboard {
  tradebook: string;
  capital_base: number;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  breakeven_trades: number;
  open_trades: number;
  flagged_excluded: number;
  win_rate: number | null;
  realized_pnl: number;
  unrealized_pnl: number;
  pnl_before_charges: number;
  pnl_after_charges: number;
  average_win_pct: number | null;
  average_loss_pct: number | null;
  biggest_win: number | null;
  biggest_win_pct: number | null;
  biggest_loss: number | null;
  biggest_loss_pct: number | null;
  profit_factor: number | null;
  gross_profit: number;
  gross_loss: number;
  expectancy: number | null;
  max_drawdown: number;
  avg_holding_days: number | null;
  trade_frequency_per_month: number | null;
  charges: Record<string, number>;
  top_winners: Bucket[];
  top_losers: Bucket[];
  most_traded: Bucket[];
  by_sector: Bucket[];
  by_strategy: Bucket[];
  equity_curve: { date: string; equity: number; pnl: number }[];
  monthly: { month: string; net_pnl: number; trades: number; win_rate: number }[];
  calendar_days: { day: string; net_pnl: number; trades: number }[];
}

export interface MonthCalendar {
  year: number;
  month: number;
  first_weekday: number;
  days_in_month: number;
  days: Record<string, { day: string; net_pnl: number; trades: number }>;
  month_pnl: number;
  month_trades: number;
}

export interface MovingAverageSetting {
  period: number;
  kind: "ema" | "sma";
  enabled: boolean;
}

export interface ChartSettings {
  timeframe: "daily" | "weekly";
  tradingview_layout: string | null;
  moving_averages: MovingAverageSetting[];
}

export interface AppearanceSettings {
  theme: "light" | "dark" | "system";
  text_scale: number;
}

export interface PositionsTableSettings {
  hidden: string[];
  group_by: "none" | "sector" | "industry" | "basic_industry";
}

export interface TradeCardSettings {
  hidden: string[];
}
